import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { SetSettings } from "./redux/actions/settings";
import { SetCurrentUser, SetUserPreferences } from "./redux/actions/user";
import { allModules, defaultRoutes, settings } from "./services/config";
import { GetPortalSettingsAPI, GetPortalStatusAPI } from "./services";
import { GetUserInfoAPI } from './services';
import Loading from './components/features/Loading.jsx';
import MainLayout from './components/layouts/MainLayout.jsx';
import AuthLayout from './components/layouts/AuthLayout.jsx';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import jwt_decode from "jwt-decode";
import Logout from './pages/authentication/Logout';
import Utype from './pages/authentication/Utype';
import Register from "./pages/authentication/Register";
import Login from "./pages/authentication/Login";
import ForgotPassword from "./pages/authentication/ForgotPassword";
import ListView from "./pages/ListView";
import DetailView from "./pages/DetailView";
import Features from './pages/Features';
import EditView from "./pages/EditView";
import Profile from "./pages/Profile";
import Dynamic from './pages/Dynamic.jsx';
import { SetLoginSettings } from './redux/actions/loginSettings.js';
import Search from './components/features/Search.jsx';

const App = () =>
{
	const [isBusy, SetBusy] = useState(true);
	const loginSettings = useSelector((state) => state.loginSettings);
	const dispatch = useDispatch();
	if(localStorage.getItem("theme"))
	{
		document.documentElement.dataset.theme = localStorage.getItem("theme");
	}
	let language = (localStorage.getItem("language")) ? localStorage.getItem("language") : 'fa_IR';
	useEffect(() =>
	{
		const tenant = localStorage.getItem("utype");
		if(tenant && tenant.length)
		{
			GetPortalStatus(tenant).then(() =>
			{
				const token = localStorage.getItem('token');
				if(token)
				{
					const decodedToken = jwt_decode(token);
					const dateNow = Date.now() / 1000;
					if(decodedToken.exp < dateNow)
					{
						localStorage.removeItem('token');
						SetBusy(false);
					}
					else
					{
						GetPortalSettings().then((result) => {
							if(result)
							{
								GetUserData(decodedToken.data.id).then((result) => {
									if(!result)
									{
										localStorage.removeItem('token');
									}
									SetBusy(false);
								});
							}
							else
							{
								localStorage.removeItem('token');
								SetBusy(false);
							}
						});
					}
				}
				else
				{
					SetBusy(false);
				}
			});
		}
		else
		{
			const host = window.location.host;
			if(host.includes('.'))
			{
				const subdomain = host.split('.')[0];
				if(subdomain && subdomain.length && !['portal', 'vision', 'tsvision', 'mobile', 'pwa'].includes(subdomain))
				{
					GetPortalStatus(subdomain).then(() =>
					{
						SetBusy(false);
					});
				}
				else
				{
					SetBusy(false);
				}
			}
			else
			{
				SetBusy(false);
			}
		}
	// eslint-disable-next-line
	}, []);

	const GetPortalStatus = async (tenant) => {
		try
		{
			const host = window.location.host;
			if(host.includes('.') && host.split('.')[0] === 'mobile')
			{
				return true;
			}
			if(tenant && tenant.length && !Object.keys(loginSettings).length)
			{
				const currentUType = localStorage.getItem('utype');
				const APIResponse = await GetPortalStatusAPI(tenant);
				if(APIResponse.status)
				{
					if(!currentUType || !currentUType.length)
					{
						localStorage.setItem('utype', tenant);
					}
					localStorage.setItem('registration', APIResponse.registration);
					localStorage.setItem('notifyby', APIResponse.NotifyBy);
					if(APIResponse.login_page)
					{
						APIResponse.login_page['registration'] = APIResponse.registration;
						dispatch(SetLoginSettings(APIResponse.login_page));
					}
				}
			}
		}
		catch (ex)
		{
			if(ex.data && ex.data.status === '401')
			{
				window.location.replace('https://app.sarvcrm.com');
			}
			else
			{
				console.error('Getting error. Please contact your administrator.');
			}
		}
	}
	
	const GetPortalSettings = async () =>
	{
		try
		{
			const APIResponse = await GetPortalSettingsAPI();
			if(Object.keys(APIResponse).length)
			{
				// ADD PORTAL MODULES TO THE ROUTES
				let routes = [...defaultRoutes];
				let activeModules = Object.keys(APIResponse.definitions.modules);
				for(let key in activeModules.reverse())
				{
					let routeObject = {};
					let vardef = APIResponse.definitions.modules[activeModules[key]];
					if(allModules[activeModules[key]])
					{
						routeObject = allModules[activeModules[key]];
						routeObject.title = (typeof vardef.label === 'object' ? vardef.label[language] : vardef.label);
					}
					else
					{
						routeObject = {
							'title': (typeof vardef.label === 'object' ? vardef.label[language] : vardef.label),
							'icon':'fa fa-star',
							'hidden':'loggedOut',
							'children': [
								{'title':'LIST', 'path':'/list/'+activeModules[key], 'icon':'fa fa-minus', 'component':'OrderList'},
								{'title':'NEW_RECORD', 'path':'/edit/'+activeModules[key], 'icon':'fa fa-minus', 'component':'OrderNew'},
							]
						}
					}
					routes.splice(1, 0, routeObject);
				}
				settings.routes = routes;
				// MERGE CRM SETTINGS & PORTAL CONFIGURATION
				dispatch(SetSettings({...settings, ...APIResponse}));
				return true;
			}
		}
		catch(ex){ return false; }
	}

	const GetUserData = async () =>
	{
		try
		{
			const APIResponse = await GetUserInfoAPI();
			if(APIResponse.id.length)
			{
				if(typeof APIResponse.preferences.theme !== 'undefined' && APIResponse.preferences.theme !== '')
				{
					localStorage.setItem("theme", APIResponse.preferences.theme);
				}
				if(typeof APIResponse.preferences.language !== 'undefined' && APIResponse.preferences.language !== '')
				{
					localStorage.setItem("language", APIResponse.preferences.language);
				}
				else if(localStorage.getItem("language") !== '')
				{
					APIResponse.preferences['language'] = localStorage.getItem("language");
				}
				if(localStorage.getItem("language") === 'en_US')
				{
					document.documentElement.dir = 'ltr';
					document.documentElement.lang = 'en_US';
				}
				else
				{
					document.documentElement.dir = 'rtl';
					document.documentElement.lang = 'fa_IR';
				}
				// USER PREFERENCES
				dispatch(SetUserPreferences(APIResponse.preferences));
				delete APIResponse.preferences;
				// CURRENT USER
				// SetUtype(APIResponse.utype);
				dispatch(SetCurrentUser(APIResponse));
				return true;
			}
		} catch(ex){ return false; }
	}

	const components =
	{
		'Dynamic': <Dynamic/>,
		'Dashboard': <Dashboard/>,
		'ListView': <ListView/>,
		'DetailView': <DetailView/>,
		'Subpanel': <DetailView/>,
		'EditView': <EditView/>,
		'Search': <Search/>,
		'Features': <Features/>,
		'Register': <Register/>,
		'Login': <Login/>,
		'ForgotPassword': <ForgotPassword/>,
		'Profile': <Profile/>,
		'NotFound': <NotFound/>,
		'Logout': <Logout/>,
	};
	return (
		<BrowserRouter>
			{isBusy ? <Loading/> :
			(!localStorage.getItem('utype') || !localStorage.getItem('utype').length) ? <AuthLayout><Utype/></AuthLayout> :
			<Routes>
				<Route path='/dashboard' element={<Navigate to='/' replace/>} exact/>
				{
				settings.routes.map(({path, component, template, children}, index) => {
					let displayComponent = <MainLayout>{components[component]}</MainLayout>;
					if(template === 'Authentication')
					{
						displayComponent = <AuthLayout>{components[component]}</AuthLayout>;
					}
					if(!path && !component && children && children.length)
					{
						children.map(({path, component}, childIndex) => {
							return <Route key={childIndex} path={path} element={<MainLayout>{components[component]}</MainLayout>} exact/>
						});
					}
					return (
						<Route key={index} path={path} element={displayComponent} exact/>
					)
				})}
			</Routes>
			}
		</BrowserRouter>
	);
}

export default App;
